import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, BookOpen, X, Bell, CheckCircle2 } from 'lucide-react';
import { useChannel } from '../contexts/ChannelContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'dl_continue_reading_v1';
const NOTIF_KEY = 'dl_notif_prompt_state_v1';
const DISMISS_KEY = 'dl_live_activity_dismissed_v1';
const SESSION_DISMISS_KEY = 'dl_live_activity_session_dismissed';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * HomepageLiveActivity
 * ───────────────────────────────────────────────
 * Apple Live-Activity-style hero card that lives on the homepage.
 * Replaces the small ContinueReadingPill with a bigger, more prominent
 * view — full hero image, big title, subtitle, "Continue watching" CTA,
 * plus an inline "Enable notifications" row when permission is unset.
 *
 * Looks intentionally similar to the Network preview dropdown:
 *   • Deep rounded corners, frosted dark card
 *   • Blue ring-glow + star sparkles
 *   • Channel-aware eyebrow + accent
 *
 * Shows when:
 *   • The user has a recent unfinished read (< 7 days) AND they're not
 *     currently on that page, OR
 *   • The user hasn't decided about notifications yet (renders a smaller
 *     "Enable notifications" variant — fallback only).
 */
const HomepageLiveActivity = () => {
  const channel = useChannel();
  const [last, setLast] = useState(null);
  const [notifGranted, setNotifGranted] = useState(false);
  const [notifDecided, setNotifDecided] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // 1. Session-level hard stop — if user dismissed the card this tab session,
    //    don't show it again until they reopen the site.
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === '1') {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    // 2. 24h persistent dismiss across sessions (existing behavior)
    try {
      const dismissedTs = localStorage.getItem(DISMISS_KEY);
      if (dismissedTs && Date.now() - parseInt(dismissedTs, 10) < 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }

    // 1. Try to read "continue reading" first (highest priority)
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const stale = !parsed?.ts || (Date.now() - parsed.ts > MAX_AGE_MS);
        if (parsed?.url && !stale) {
          try {
            const onSamePath = window.location.pathname === new URL(parsed.url, window.location.origin).pathname;
            if (!onSamePath) {
              setLast({ ...parsed, _mode: 'continue' });
              return; // we have something — done
            }
          } catch { setLast({ ...parsed, _mode: 'continue' }); return; }
        } else if (stale) {
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }
      }
    } catch { /* ignore */ }

    // 2. Fallback: fetch the latest featured story from the current channel and
    //    surface it as a "New story · Tap to read" Live Activity. Always visible.
    (async () => {
      try {
        const params = channel.id === 'flyt' ? '?channel=flyt&featured=true&limit=1' :
                       channel.id === 'dreams_visionary' ? '?channel=dreams_visionary&limit=1&featured=true' :
                       '?featured=true&limit=1';
        const res = await axios.get(`${API}/stories${params}`);
        const items = res.data || [];
        const story = items[0];
        if (!story) return;
        const storyPath = `${channel.storiesPath}/${story.slug}`;
        setLast({
          slug: story.slug,
          title: (story.name || '').toUpperCase(),
          channelLabel: channel.label,
          url: storyPath,
          image: story.cover_image || story.thumbnail,
          _mode: 'new',
        });
      } catch { /* silent — no card to show */ }
    })();
  }, [channel.id, channel.label, channel.storiesPath]);

  // Separately track notification state (used for the optional Bell button on
  // the right side of the card).
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    setNotifGranted(Notification.permission === 'granted');
    let stored = null;
    try { stored = localStorage.getItem(NOTIF_KEY); } catch { /* ignore */ }
    setNotifDecided(Notification.permission !== 'default' || !!stored);
  }, []);

  const path = (() => {
    if (!last?.url) return null;
    try { return new URL(last.url, window.location.origin).pathname; }
    catch { return last.url; }
  })();

  const dismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    // Persist the dismiss for both this session AND the next 24h on this device.
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* ignore */ }
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setLast(null);
    setDismissed(true);
  };

  const allowNotifications = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!('Notification' in window)) return;
    try {
      const result = await Notification.requestPermission();
      try { localStorage.setItem(NOTIF_KEY, result); } catch { /* ignore */ }
      setNotifDecided(true);
      setNotifGranted(result === 'granted');
      if (result === 'granted') {
        try {
          // eslint-disable-next-line no-new
          new Notification('You’re in.', {
            body: `${channel.label} notifications are on. We’ll ping you when a new story drops.`,
            icon: '/favicon.ico',
          });
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  };

  // If there's nothing to show, render nothing.
  if (dismissed) return null;
  if (!last && notifDecided) return null;

  // Variant A — big "continue watching" hero card (preferred)
  if (last) {
    return (
      <section className="w-full bg-black" data-testid="homepage-live-activity">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <div
            className="relative rounded-[28px] overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10"
            style={{ animation: 'fadeSlideUp 520ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          >
            {/* Decorative blue glow ring (mirrors Network preview dropdown) */}
            <div className="absolute -right-32 top-1/2 -translate-y-1/2 w-[420px] h-[420px] pointer-events-none">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/40 via-blue-600/15 to-transparent blur-2xl" />
              <div
                className="absolute inset-8 rounded-full border border-blue-300/30"
                style={{ boxShadow: '0 0 60px 8px rgba(59,130,246,0.35) inset' }}
              />
              <div className="absolute inset-16 rounded-full border border-blue-300/20 opacity-60" />
            </div>
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(1px 1px at 18% 30%, white, transparent), radial-gradient(1px 1px at 78% 25%, white, transparent), radial-gradient(1px 1px at 55% 75%, white, transparent), radial-gradient(1px 1px at 40% 85%, white, transparent)',
              }}
            />

            <Link
              to={path}
              className="relative flex items-stretch gap-5 sm:gap-6 p-4 sm:p-5"
              data-testid="homepage-live-activity-link"
            >
              {/* Hero image, prominent */}
              {last.image ? (
                <img loading="lazy" decoding="async"
                  src={last.image}
                  alt=""
                  className="w-[110px] sm:w-[180px] lg:w-[220px] aspect-square sm:aspect-[4/3] rounded-2xl object-cover ring-1 ring-white/10 flex-shrink-0"
                />
              ) : (
                <div className="w-[110px] sm:w-[180px] aspect-square rounded-2xl bg-blue-500/15 flex items-center justify-center ring-1 ring-white/10 flex-shrink-0">
                  <BookOpen className="w-8 h-8 text-blue-300" />
                </div>
              )}
              <div className="flex-1 min-w-0 flex flex-col justify-center text-white pr-8">
                <p className="font-italic text-[10px] sm:text-xs uppercase tracking-widest text-blue-300 mb-2 inline-flex items-center gap-2">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500" />
                  </span>
                  {last.channelLabel || 'Dreams League'} · Continue
                </p>
                <p className="font-display uppercase text-xl sm:text-2xl lg:text-3xl text-white font-bold leading-[1.05] line-clamp-2">
                  {last.title}
                </p>
                <div className="mt-3 sm:mt-4 inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold text-xs sm:text-sm">
                    <Play className="w-3.5 h-3.5 fill-black" />
                    Continue watching
                  </span>
                  {('Notification' in window) && !notifGranted && !notifDecided && (
                    <button
                      type="button"
                      onClick={allowNotifications}
                      className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm font-bold transition"
                      data-testid="live-activity-allow-notif"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      Allow notifications
                    </button>
                  )}
                  {notifGranted && (
                    <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-green-300/90">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Notifications on
                    </span>
                  )}
                </div>
              </div>
            </Link>

            {/* Close X — sibling of <Link>, NOT a child, so clicks here
                cannot bubble into the Link's navigation. Touch + click
                bound directly with stopPropagation to also kill iOS Safari
                "ghost clicks". */}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(e); }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(e); }}
              aria-label="Dismiss"
              className="absolute top-2 right-2 sm:top-3 sm:right-3 w-11 h-11 rounded-full bg-black/60 hover:bg-white/20 flex items-center justify-center text-white/90 hover:text-white transition z-30 touch-manipulation cursor-pointer"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              data-testid="homepage-live-activity-dismiss"
            >
              <X className="w-4 h-4 pointer-events-none" />
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Variant B — fallback "enable notifications" full card (only when no continue-reading)
  return (
    <section className="w-full bg-black" data-testid="homepage-live-activity-notif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
        <div
          className="relative rounded-[28px] overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] ring-1 ring-white/10 bg-black"
          style={{ animation: 'fadeSlideUp 520ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        >
          <div className="absolute -right-24 top-1/2 -translate-y-1/2 w-[360px] h-[360px] pointer-events-none">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/40 via-blue-600/15 to-transparent blur-2xl" />
            <div
              className="absolute inset-6 rounded-full border border-blue-300/30"
              style={{ boxShadow: '0 0 60px 8px rgba(59,130,246,0.35) inset' }}
            />
          </div>
          <div className="relative px-5 py-5 sm:px-6 sm:py-6 max-w-[78%] text-white">
            <p className="font-italic text-xs uppercase tracking-widest text-blue-300 mb-2 inline-flex items-center gap-2">
              <Bell className="w-3.5 h-3.5" />
              {channel.label} · Notifications
            </p>
            <p className="font-display text-xl sm:text-2xl uppercase leading-[1.05] text-white font-bold mb-2">
              Get pinged when a new story drops
            </p>
            <p className="text-sm text-gray-300 mb-4 leading-snug max-w-md">
              Athlete sit-downs, breaking interviews, exclusive video — we'll let you know the moment it goes live.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={allowNotifications}
                className="px-4 py-2 rounded-full bg-white text-black font-bold text-xs sm:text-sm hover:bg-gray-200 transition"
                data-testid="live-activity-allow-btn"
              >
                Allow notifications
              </button>
              <button
                type="button"
                onClick={() => {
                  try { localStorage.setItem(NOTIF_KEY, 'dismissed'); } catch { /* ignore */ }
                  setNotifDecided(true);
                }}
                className="px-3 py-2 rounded-full text-gray-400 hover:text-white text-xs sm:text-sm font-bold transition"
                data-testid="live-activity-not-now"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem(NOTIF_KEY, 'dismissed'); } catch { /* ignore */ }
              setNotifDecided(true);
            }}
            aria-label="Dismiss"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition z-10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomepageLiveActivity;
